import { DocTypeEnum, DocumentTypeEnum, PageTypeEnum } from '@/constant';
import { TDocumentUploadDefineItem } from '@/services';
import { TDefineDocumentAreaDetailStore } from '@/stores';
import { ImgHTMLAttributes } from 'react';

import { ModalProps } from 'antd';

import * as Yup from 'yup';

import { AutoCompleteFormProps } from '../common/autocomplete-form';
import { CachetFormProps } from '../common/cachet-form';
import { CheckboxFormProps } from '../common/checkbox-form';
import { TriStateValueEnum } from '../common/checkbox-tri-state';
import { DatePickerFormProps } from '../common/date-picker-form';
import { DateSegmentPickerFormProps } from '../common/date-segment-picker-form';
import { InputFileFormProps } from '../common/input-file-form';
import { RangePickerFormProps } from '../common/range-picker-form';
import { SelectFormProps } from '../common/select-form';
import { SignatureProps } from '../common/signature';
import { TextareaProps } from '../common/text-area-form/text-area-form.type';
import { TimePickerFormProps } from '../common/time-picker-form';
import { REDUX_JSON } from './define-document-write.config';
import { getReferenceData } from './define-document-write.helper';
/**
 * Description
 *
 * CREATE: open from button create in define-document-upload page
 *
 * UPDATE: open from icon preview in update define-document-upload page
 *
 * REPLACE: open from icon preview in list
 *
 * AUTO: open from issue-application-download to auto print
 *
 * ISSUE-APPLICATION-DOWNLOAD: open from  issue-application-download page
 */
export enum DefineDocumentWriteModeEnum {
	CREATE = 'CREATE',
	UPDATE = 'UPDATE',
	REPLACE = 'REPLACE',
	AUTO = 'AUTO',
	ISSUE_APPLICATION_DOWNLOAD = 'ISSUE-APPLICATION-DOWNLOAD',
}

export type PayloadUploaded = {
	fileUploaded?: TDocumentUploadDefineItem;
	rowId?: number;
	mode?: DefineDocumentWriteModeEnum;
};

export type IProps = ModalProps & {
	open: boolean;
	onCloseModal(): void;
	documentType: DocumentTypeEnum;
	rowId: number;
	onUploaded(data?: PayloadUploaded): void;
	refTableName?: PageTypeEnum;
	mappingList?: {
		id?: number;
		refTableName: PageTypeEnum;
		refTableId: number;
	}[];
	fileId?: number;
	mode?: DefineDocumentWriteModeEnum;
	refTableId?: number;
	hasEmptyErrors?: boolean;
	dataViewIssueApplicant?: TDefineDocumentAreaDetailStore[];
};

export type WrapDocumentProps = Omit<IProps, 'open'> & {
	title: string;
	initDataFromIssueApplication?: any;
	fileAuto?: boolean;
};

export type WrapDocumentRef = {
	onCancel(): void;
};

export type DefineDocumentWriteButtonProps = Omit<
	IProps,
	'open' | 'onCloseModal'
> & {
	className?: string;
	label?: string;
	link?: boolean;
	readonly?: boolean;
	fileAuto?: boolean;
};

export enum CheckboxType {
	SINGLE = 'SINGLE',
	MULTIPLE = 'MULTIPLE',
	SELECT_ALL = 'SELECT_ALL',
}

type ArrayElement<T> = T extends Array<infer U> ? U : T;
type ElementKeys<T, K extends keyof T> = K extends keyof T
	? T[K] extends Array<any>
		? keyof ArrayElement<T[K]>
		: never
	: never;

type BaseConfig<T extends Record<string, any>> = {
	name: keyof T;
};

type EnhancedConfig<
	T extends Record<string, any>,
	K extends keyof T = keyof T,
> =
	| {
			repeat: number;
			jsonKey: K;
			name: ElementKeys<T, K>;
	  }
	| {
			jsonKey?: undefined;
			name: keyof T;
	  };

type TextAreaBaseConfig<T extends Record<string, any>> = TextareaProps &
	BaseConfig<T>;

type TextAreaEnhancedConfig<
	T extends Record<string, any>,
	K extends keyof T = keyof T,
> = (
	values: T,
	index: number
) => Omit<TextAreaBaseConfig<T>, 'name'> & EnhancedConfig<T, K>;

export type TextAreaConfig<T extends Record<string, any>> =
	| TextAreaBaseConfig<T>
	| TextAreaEnhancedConfig<T>;

type CheckboxBaseConfig<T extends Record<string, any>> = BaseConfig<T> & {
	type?: CheckboxType;
	options: string[];
	classNames?: string[];
	className?: string;
	getResetKey?: (currentSelectKey: string) => string[];
	disabled?: boolean;
} & Pick<CheckboxFormProps, 'iconType' | 'iconClassName'>;

type CheckboxEnhancedConfig<
	T extends Record<string, any>,
	K extends keyof T = keyof T,
> = (
	values: T,
	index: number
) => Omit<CheckboxBaseConfig<T>, 'name'> &
	EnhancedConfig<T, K> & {
		type?: CheckboxType;
		options: string[];
		classNames?: string[];
		className?: string;
		getResetKey?: (currentSelectKey: string) => string[];
		disabled?: boolean;
	} & Pick<CheckboxFormProps, 'iconType' | 'iconClassName'>;

export type CheckboxConfig<T extends Record<string, any>> =
	| CheckboxBaseConfig<T>
	| CheckboxEnhancedConfig<T>;

export type TriStateCheckboxConfig<T extends Record<string, any>> =
	BaseConfig<T> & {
		classNames?: string[];
		options?: TriStateValueEnum[];
	};

type SignatureBaseConfig<T extends Record<string, any>> = Omit<
	SignatureProps,
	'name' | 'dataUrl' | 'ref'
> &
	BaseConfig<T>;

type SignatureEnhancedConfig<
	T extends Record<string, any>,
	K extends keyof T = keyof T,
> = (
	values: T,
	index: number
) => Omit<SignatureBaseConfig<T>, 'name'> & EnhancedConfig<T, K>;

export type SignatureConfig<
	T extends Record<string, any> = Record<string, any>,
> = SignatureBaseConfig<T> | SignatureEnhancedConfig<T>;

type DatePickerBaseConfig<T extends Record<string, any>> = Omit<
	DatePickerFormProps,
	'name' | 'value' | 'ref'
> &
	Pick<
		DateSegmentPickerFormProps,
		| 'segmentedInputs'
		| 'yearClassName'
		| 'monthClassName'
		| 'dayClassName'
		| 'placeholders'
	> &
	BaseConfig<T>;

type DatePickerEnhancedConfig<
	T extends Record<string, any>,
	K extends keyof T = keyof T,
> = (
	values: T,
	index: number
) => Omit<DatePickerBaseConfig<T>, 'name'> & EnhancedConfig<T, K>;

export type DatePickerConfig<T extends Record<string, any>> =
	| DatePickerBaseConfig<T>
	| DatePickerEnhancedConfig<T>;

type SelectBaseConfig<T extends Record<string, any>> = Omit<
	SelectFormProps,
	'name' | 'value' | 'ref'
> &
	BaseConfig<T>;

type SelectEnhancedConfig<
	T extends Record<string, any>,
	K extends keyof T = keyof T,
> = (
	values: T,
	index: number
) => Omit<SelectBaseConfig<T>, 'name'> &
	EnhancedConfig<T, K> & {
		resetKeys?: ElementKeys<T, K>[];
	};

export type SelectConfig<T extends Record<string, any>> =
	| SelectBaseConfig<T>
	| SelectEnhancedConfig<T>;

type InputFileBaseConfig<T extends Record<string, any>> = Omit<
	InputFileFormProps,
	'name' | 'value' | 'ref'
> &
	BaseConfig<T>;

export type InputFileConfig<T extends Record<string, any>> =
	InputFileBaseConfig<T>;

type RangePickerBaseConfig<T extends Record<string, any>> = Omit<
	RangePickerFormProps,
	'name' | 'value' | 'ref'
> & {
	name: Array<keyof T>;
};

export type RangePickerConfig<T extends Record<string, any>> =
	RangePickerBaseConfig<T>;

type TimePickerBaseConfig<T extends Record<string, any>> = Omit<
	TimePickerFormProps,
	'name' | 'value' | 'ref'
> &
	BaseConfig<T>;

type TimePickerEnhancedConfig<
	T extends Record<string, any>,
	K extends keyof T = keyof T,
> = (values: T) => Omit<TimePickerBaseConfig<T>, 'name'> & EnhancedConfig<T, K>;

export type TimePickerConfig<T extends Record<string, any>> =
	| TimePickerBaseConfig<T>
	| TimePickerEnhancedConfig<T>;

type BackgroundConfig = ImgHTMLAttributes<any> & {
	dataSource?: string;
};

type AutocompleteBaseConfig<T extends Record<string, any>> =
	AutoCompleteFormProps & BaseConfig<T>;

type AutocompleteEnhancedConfig<
	T extends Record<string, any>,
	K extends keyof T = keyof T,
> = (
	values: T,
	index: number
) => Omit<AutocompleteBaseConfig<T>, 'name'> & {
	repeat: number;
	jsonKey: K;
	name: ElementKeys<T, K>;
	options: any[];
};

export type AutocompleteConfig<T extends Record<string, any>> =
	| AutocompleteBaseConfig<T>
	| AutocompleteEnhancedConfig<T>;

export type CachetFormConfig = CachetFormProps;

type HighlightBoxProps<T extends Record<string, any>> = {
	name?: keyof T;
	names?: Array<keyof T>;
	className?: string;
	ignoreValidate?: boolean;
	hasError?: boolean;
	path?: string[];
};

type HighlightBoxBaseConfig<T extends Record<string, any>> =
	HighlightBoxProps<T>;

type HighlightBoxEnhancedConfig<T extends Record<string, any>> = (
	values: T,
	index: number
) => HighlightBoxBaseConfig<T>;

export type HighlightBoxConfig<T extends Record<string, any>> =
	| HighlightBoxBaseConfig<T>
	| HighlightBoxEnhancedConfig<T>;

export type PageConfig<T extends Record<string, any>> = {
	background: BackgroundConfig;
	textAreas?: TextAreaConfig<T>[];
	checkboxes?: CheckboxConfig<T>[];
	signatures?: SignatureConfig<T>[];
	datePickers?: DatePickerConfig<T>[];
	selects?: SelectConfig<T>[];
	inputFiles?: InputFileConfig<T>[];
	rangePickers?: RangePickerConfig<T>[];
	timePickers?: TimePickerConfig<T>[];
	autocompletes?: AutocompleteConfig<T>[];
	cachetForms?: CachetFormConfig[];
	highlightBoxes?: HighlightBoxConfig<T>[];
	triStateCheckboxes?: TriStateCheckboxConfig<T>[];
};

type Awaited<T> = T extends Promise<infer U> ? U : T;

type GetReferenceData = Awaited<ReturnType<typeof getReferenceData>>;

export type PageDataUnion<DocType extends keyof typeof REDUX_JSON = any> =
	DocType extends keyof typeof REDUX_JSON
		? {
				docType: DocType;
				detailData: ReturnType<(typeof REDUX_JSON)[DocType]>;
			}
		: {
				docType: DocType;
				detailData: any;
			};

export type PagesResolverContext = {
	refTableName?: PageTypeEnum;
};

export type PagesConfig<T extends Record<string, any>> =
	| PageConfig<T>[]
	| ((ctx: PagesResolverContext) => PageConfig<T>[]);

export type FormConfig<
	T extends Record<string, any>,
	DocType extends DocTypeEnum = any,
> = {
	title: string;
	initializeValue: T;
	pages: PagesConfig<T>;
	mapPropToValues?(
		props: PageDataUnion<
			DocType extends keyof typeof REDUX_JSON ? DocType : any
		> &
			GetReferenceData & {
				refTableName?: PageTypeEnum;
				documentType: DocumentTypeEnum;
			}
	): Partial<T>;
	validateOnSave?: (data: T) => {
		hasError: boolean;
		message?: string;
	};
	validationSchema?: Yup.ObjectSchema<any>;
	/**
	 * Fields that are auto-computed (derived) from other fields in `data`.
	 * Rules are declared per config; the shared layer only applies them
	 * generically (without knowing any specific field name).
	 * - `when`: apply the rule whenever one of these fields changes.
	 * - `set`: the target field to write the value to.
	 * - `compute`: receives the current `data` and returns the new value for `set`.
	 * Two-way relationships are supported via multiple rules with different
	 * `when`/`set` pairs.
	 */
	derivedFields?: {
		when: string[];
		set: keyof T & string;
		compute: (data: Record<string, any>) => string;
	}[];
};
